<template>
    <div class="feature-image">
        <div
            :data-aos="isImgLeft ? 'fade-left' : 'fade-right'"
            data-aos-duration="2000"
            v-bind:class="[isImgLeft ? 'row-reverse' : '']"
        >
            <!-- Text side. The per-section animated decoration is
                 injected INTO the title string via v-html (see
                 `decoratedTitle` computed below), so it renders as
                 inline content inside the h1, sitting on the title's
                 own text line. Position (before/after the title text)
                 rotates by section index so the page feels alive as
                 you scroll. -->
            <div class="feature-image__text">
                <span
                    v-if="kicker"
                    class="kicker feature-image__kicker"
                >{{ kicker }}</span>
                <Teaser
                    :title="withDecoration ? decoratedTitle : data?.title"
                    :description="data?.description"
                    :buttons="data?.buttons"
                />
            </div>
            <div class="feature-image__media">
                <!-- Loading strategy keyed off `index`:
                     - index 0 is the hero (above the fold on both Home
                       and the Features page) → load eagerly with high
                       fetch priority so it doesn't delay LCP.
                     - index > 0 sits below the fold → lazy-load so it
                       doesn't compete for bandwidth on first paint.
                     decoding="async" keeps image decode off the main
                     thread either way. -->
                <img
                    v-if="data?.img?.url"
                    :src="data?.img?.url"
                    :alt="data?.img?.alternativeText || 'Bzioup feature illustration'"
                    :loading="index === 0 ? 'eager' : 'lazy'"
                    :fetchpriority="index === 0 ? 'high' : 'auto'"
                    decoding="async"
                />
                <HeartbeatCard v-else-if="withDecoration && data?.title" />
            </div>
        </div>
    </div>
</template>

<script>
import Teaser from '@/components/Text/Teaser.vue'
import HeartbeatCard from '@/components/Sections/HeartbeatCard.vue'

const DECORATIONS = ['sparkle', 'heart', 'dot']

/** Glyph for each decoration variant. Kept tiny and inline so it
 *  reads as part of the title line, not as a separate badge. The
 *  `dot` variant uses an empty span (the dot itself is drawn via
 *  CSS background + radar-ping box-shadow). */
const GLYPHS = {
    sparkle: '✦', // ✦
    heart: '♥',   // ♥
    dot: '',
}

export default {
    name: 'FeatureImage',
    components: {
        Teaser,
        HeartbeatCard,
    },
    props: {
        data: Object,
        isImgLeft: Boolean,
        index: { type: Number, default: 0 },
        /** Opt-in flag for the per-section animated decoration
         *  (sparkle / heart / dot). Off by default because the same
         *  FeatureImage component is also used on the Home page,
         *  where the badge would feel out of place. The Features
         *  page passes :with-decoration="true" explicitly. */
        withDecoration: { type: Boolean, default: false },
        kicker: String,
    },
    computed: {
        decoration() {
            return DECORATIONS[this.index % DECORATIONS.length]
        },
        /** First two sections keep the decoration BEFORE the title
         *  text; sections from index 2 onward flip it to AFTER. Adds
         *  rhythm so the decoration "slides" across the page as you
         *  scroll. */
        decorationAfter() {
            return this.index >= 2
        },
        /** Title string with the decoration span injected as an
         *  inline sibling of the title text. Teaser renders the
         *  title via v-html, so this HTML lands inside the h1 and
         *  the decoration sits ON the title's own text line (no
         *  flex / absolute positioning needed). Scoped styles do
         *  not penetrate v-html content, so the decoration is
         *  styled via :deep(.fi-deco) below. */
        decoratedTitle() {
            const variant = this.decoration
            const glyph = GLYPHS[variant] || ''
            const span = `<span class="fi-deco fi-deco--${variant}" aria-hidden="true">${glyph}</span>`
            const title = this.data?.title || ''
            return this.decorationAfter
                ? `${title}\u00A0${span}`
                : `${span}\u00A0${title}`
        },
    },
}
</script>

<style lang="scss" scoped>
.feature-image {
    overflow: hidden;
}

.feature-image>div {
    margin: auto;
    width: 100%;
}

.feature-image img {
    /* Mobile-first: cap the image height modestly so a tall/portrait
       feature image doesn't swallow the phone viewport (was 500px,
       which on a phone pushed everything else off-screen). Scales up
       through the breakpoints below. `display: block` + auto inline
       margins center it in the stacked mobile layout. */
    display: block;
    width: auto;
    max-width: 100%;
    max-height: 300px;
    margin-inline: auto;
    border-radius: $border-radius;
    transition: transform 320ms cubic-bezier(0.2, 0.8, 0.3, 1),
                box-shadow 320ms ease-out;
}

.feature-image:hover img {
    transform: translateY(-4px) scale(1.01);
    box-shadow: var(--bzioup-shadow-soft);
}


/* Pink underline under the section title (Teaser h1). Stays under
   the title even when the row alternates left/right. Grows from 0
   to 56px on hover for a quiet "alive" cue. */
.feature-image :deep(.text-teaser h1) {
    position: relative;
    padding-bottom: 12px;
}

.feature-image :deep(.text-teaser h1)::after {
    content: '';
    position: absolute;
    /* Mobile: the title is centered, so the dash centers under it
       (left:0 left a lonely dash at the screen edge). Snaps back to
       the left rag at $mq-medium where the text goes left-aligned. */
    left: 50%;
    transform: translateX(-50%);
    bottom: 0;
    width: 32px;
    height: 3px;
    border-radius: 2px;
    background: var(--bzioup-color-pink);
    transition: width 320ms cubic-bezier(0.2, 0.8, 0.3, 1);
}

@media (min-width: $mq-medium) {
    .feature-image :deep(.text-teaser h1)::after {
        left: 0;
        transform: none;
    }
}

.feature-image:hover :deep(.text-teaser h1)::after {
    width: 56px;
}

/* Decoration injected INTO the h1 via v-html (see decoratedTitle
   computed). Lives inside the title element as inline content, so
   it shares the title's text line natively. No flex, no absolute
   positioning. Scoped styles cannot pierce v-html content, hence
   :deep(). */
.feature-image :deep(.fi-deco) {
    display: inline-block;
    color: var(--bzioup-color-pink);
    /* Knock the decoration down from the title's font-size to a
       size that reads as a "small mark next to the title" rather
       than a second glyph in the headline. */
    font-size: 0.55em;
    line-height: 1;
    /* Pull up a hair so the small glyph's optical center aligns
       with the title's x-height rather than sitting on the
       baseline (which makes a small glyph look like it sagged). */
    vertical-align: 0.35em;
    transform-origin: center;
}

/* Variant 1: sparkle. Rotating + scaling glyph. Same rhythm as the
   BETA badge in the mobile app and the "Popular" pill on /plans. */
.feature-image :deep(.fi-deco--sparkle) {
    animation: feature-decoration-sparkle 2s ease-in-out infinite;
}
@keyframes feature-decoration-sparkle {
    0%, 100% { transform: rotate(0deg) scale(1); }
    50%      { transform: rotate(180deg) scale(1.18); }
}

/* Variant 2: heart. Two-beat pulse (heartbeat). Slightly slower
   than the sparkle so adjacent sections don't visually sync. */
.feature-image :deep(.fi-deco--heart) {
    font-size: 0.6em;
    animation: feature-decoration-heart 2.4s ease-in-out infinite;
}
@keyframes feature-decoration-heart {
    0%, 100% { transform: scale(1); }
    14%      { transform: scale(1.20); }
    28%      { transform: scale(0.96); }
    42%      { transform: scale(1.10); }
    56%      { transform: scale(1.00); }
}

/* Variant 3: dot. The glyph span is empty. the dot is drawn as a
   CSS background, with a concentric pulse ring (radar ping) via
   box-shadow. Slowest rhythm of the three. */
.feature-image :deep(.fi-deco--dot) {
    width: 0.45em;
    height: 0.45em;
    border-radius: 50%;
    background: var(--bzioup-color-pink);
    box-shadow: 0 0 0 0 rgba(218, 138, 139, 0.55);
    /* Nudge a bit higher so the small filled dot reads as level
       with the title's x-height rather than sitting low. */
    vertical-align: 0.55em;
    animation: feature-decoration-dot 2.8s ease-out infinite;
}
@keyframes feature-decoration-dot {
    0%   { box-shadow: 0 0 0 0    rgba(218, 138, 139, 0.55); }
    70%  { box-shadow: 0 0 0 14px rgba(218, 138, 139, 0); }
    100% { box-shadow: 0 0 0 0    rgba(218, 138, 139, 0); }
}

/* Gap between the text block and the image in the stacked mobile
   layout. 50px read as a lot of dead space on a phone; 32px keeps
   the two clearly separated without the section feeling sparse.
   Reset to 0 at $mq-medium where they sit side-by-side. */
.feature-image>div>div:nth-child(1) {
    margin-bottom: 32px;
}

/* Mobile reading order: title / image / description / buttons.
   display:contents flattens the text and media wrappers so the h1,
   img, h3 and buttons become directly orderable flex items. */
@media (max-width: ($mq-medium - 1)) {
    .feature-image>div {
        display: flex;
        flex-direction: column;
        gap: 24px;
    }

    .feature-image__text,
    .feature-image__media,
    .feature-image :deep(.text-teaser) {
        display: contents;
    }

    .feature-image__kicker {
        order: 0;
        margin: 0 auto;
    }

    .feature-image :deep(.text-teaser h1) {
        order: 1;
        margin: 0;
    }

    .feature-image__media > * {
        order: 2;
    }

    .feature-image :deep(.text-teaser h3) {
        order: 3;
        margin: 0;
    }

    .feature-image :deep(.text-teaser>div) {
        order: 4;
        margin-top: 0;
    }
}

@media (min-width: $mq-small) {
    .feature-image>div {
        width: 80%;
    }

    .feature-image img {
        width: auto;
        /* Tablet / large phone: step the cap up from the 300px
           mobile value toward the 500px desktop value. */
        max-height: 400px;
    }
}

@media (min-width: $mq-medium) {
    .feature-image {
        display: flex;
        align-items: center;
        justify-content: center;

        >div {
            display: flex;
            align-items: center;
            justify-content: center;
        }

        >div.row-reverse {
            flex-direction: row-reverse;
        }

        >div>div {
            max-width: 400px;
            display: inline-block;
        }

        >div>div:nth-child(1) {
            text-align: left;
            margin-left: 0px;
            margin-right: 80px;
        }

        >div.row-reverse>div:nth-child(1) {
            margin-left: 80px;
            margin-right: 0px;
        }

        >div>div:nth-child(1) {
            margin-bottom: 0px;
        }

        img {
            width: auto;
            max-height: 500px;
        }
    }
}
</style>
